import Head from "next/head";
import { useEffect, useState } from "react";
import { fetchGlobalSettings } from "@/lib/globalSettings";

/**
 * Build a Schema.org Person object from the structured author shape the
 * admin API returns (id, name, slug, username, bio, credentials, avatar_url,
 * social_links, same_as). Falls back gracefully when fields are missing.
 */
function buildAuthorPerson(author) {
  const name = String(author.name || "").trim();
  const person = { "@type": "Person", name };
  // No /author/[slug] route — skip person.url to avoid emitting a dead link.
  // Credentials, sameAs, alternateName, and image still convey E-E-A-T.
  if (author.username) person.alternateName = String(author.username);
  const sameAs = Array.isArray(author.same_as)
    ? author.same_as.filter((u) => !!u)
    : [];
  if (sameAs.length) person.sameAs = sameAs;
  if (author.credentials) person.jobTitle = String(author.credentials);
  if (author.avatar_url) person.image = String(author.avatar_url);
  return person;
}

/**
 * Single source of truth for SEO <head> markup.
 *
 * Drives every meta tag from the seo_meta row in the CMS (see
 * admin/database/migration.sql lines 183-220). Accepts either the full
 * normalized record (with top-level seo_title, meta_description, ...) or
 * the nested r.seo sub-object as exposed by the admin API.
 *
 * Usage:
 *   <SeoHead
 *     seo={page}                          // page or post object from cms.js
 *     type="article"                      // "article" | "website"
 *     path={`/blog/${post.slug}`}         // used for the computed canonical
 *     fallbackTitle={post.title}          // used when seo_title is empty
 *     fallbackDescription={post.excerpt}
 *     extraKeywords={[...post.tag, ...post.category_names]}
 *     publishedTime={post.published_at}
 *     authorName={post.author.name}        // OR
 *     author={post.author}                 // structured: { id, name, slug,
 *                                          //   username, bio, credentials,
 *                                          //   avatar_url, social_links,
 *                                          //   same_as } — emits a Person
 *                                          //   JSON-LD block (E-E-A-T)
 *     ogImageOverride={post.featured_image} // optional explicit OG image
 *     publisherName="Siddhivinayak Group"   // shown as article:publisher and
 *                                           // merged into JSON-LD publisher
 *                                           // when seo.schema_json omits it
 *   />
 */
const SeoHead = ({
  seo = {},
  type = "website",
  path = "/",
  fallbackTitle = "",
  fallbackDescription = "",
  fallbackImage = "",
  extraKeywords = [],
  publishedTime = null,
  authorName = null,
  author = null,
  sectionName = null,
  tagNames = [],
  ogImageOverride = null,
  siteName: siteNameProp = "",
  locale: localeProp = "",
  publisherName: publisherNameProp = "",
}) => {
  const [siteNameSetting, setSiteNameSetting] = useState("");
  const [localeSetting, setLocaleSetting] = useState("");
  useEffect(() => {
    if (siteNameProp || localeProp) return;
    let alive = true;
    fetchGlobalSettings().then((s) => {
      if (!alive || !s) return;
      if (s.site_name) setSiteNameSetting(s.site_name);
      if (s.site_locale) setLocaleSetting(s.site_locale);
    });
    return () => { alive = false; };
  }, [siteNameProp, localeProp]);

  const siteName = siteNameProp || siteNameSetting || "Siddhivinayak Group";
  const locale = localeProp || localeSetting || "en_US";
  const publisherName =
    publisherNameProp || siteNameProp || siteNameSetting || siteName || "";

  // Author: prefer the structured object (carries sameAs, credentials, etc.)
  // for JSON-LD Person emission. Fall back to the plain `authorName` string
  // when only that is supplied — keeps existing call sites working.
  const authorObj =
    author && typeof author === "object" && !Array.isArray(author)
      ? author
      : null;
  const effectiveAuthorName =
    (authorObj && authorObj.name) || authorName || "";

  // Canonical/og:url base. NEXT_PUBLIC_SITE_URL is inlined at build time,
  // so the production domain must be set in .env.local. The hardcoded
  // fallback below is the production apex (matches the URL field on
  // pages where seo.canonical_url is empty) — never localhost, which
  // leaks dev URLs into SERP audits.
  const siteUrl = (
    process.env.NEXT_PUBLIC_SITE_URL || "https://siddhivinayakinfra.in"
  ).replace(/\/$/, "");
  const absolute = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${siteUrl}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // ─── Title ───
  const seoTitle = (seo.seo_title || fallbackTitle || "").toString();
  const seoDescription =
    (seo.meta_description || fallbackDescription || "").toString();

  // ─── Keywords: focus + secondary + extra (case-insensitive dedupe, cap 8) ───
  // SEO audit tools flag three anti-patterns here:
  //   1. Duplicates (handled by the lowercase `seen` set).
  //   2. Emitting the full <title> verbatim as a keyword — every keyword
  //      meta is supposed to be a search term a user would type, not the
  //      page's own title. Pump a normalised form of the title into the
  //      dedup set so any extraKeyword equal to it (or a case-insensitive
  //      match) is silently dropped.
  //   3. Multi-keyword strings glued with a separator — they read as a
  //      single phrase to the meta tag and confuse audits. Splitting on
  //      `|` / `,` before dedup catches both halves independently.
  const titleKey = seoTitle.trim().toLowerCase();
  const seen = new Set();
  if (titleKey) seen.add(titleKey);
  const out = [];
  const dedupePush = (val) => {
    const raw = (val || "").toString().trim();
    if (!raw) return;
    // Split on common separators so a string like "x | y" surfaces as
    // both x and y; dedup then collapses duplicates across splits.
    const parts = raw
      .split(/[|,]/g)
      .map((p) => p.trim())
      .filter(Boolean);
    for (const part of parts) {
      const key = part.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(part);
    }
  };
  dedupePush(seo.focus_keyword);
  if (Array.isArray(seo.secondary_keywords)) {
    seo.secondary_keywords.forEach(dedupePush);
  }
  if (Array.isArray(extraKeywords)) {
    extraKeywords.forEach(dedupePush);
  }
  const seoKeywords = out.slice(0, 8).join(", ");

  // ─── Canonical ───
  // Honor an admin-provided canonical_url, but only if it points at the
  // *current* site origin. Stale entries (e.g. the old siddhivinayakgroup.com
  // domain saved before a rebrand, or http://localhost:3000 baked in by
  // a developer's local seed) would otherwise leak into SERPs as the
  // page-level canonical and create cross-domain duplicate-content
  // signals — much worse than just letting SeoHead compute it. Empty /
  // relative / cross-origin values fall through to the computed URL.
  const computedCanonical = `${siteUrl}${path.startsWith("/") ? "" : "/"}${path}`;
  let canonical = computedCanonical;
  const override = String(seo.canonical_url || "").trim();
  if (override) {
    try {
      const parsed = new URL(override);
      if (parsed.origin === siteUrl) {
        canonical = override;
      }
    } catch (_) {
      // Not an absolute URL — ignore the override and keep computed.
    }
  }

  // ─── robots ───
  const ra =
    seo.robots_advanced && typeof seo.robots_advanced === "object"
      ? seo.robots_advanced
      : {};
  const robotsParts = [
    seo.robots_index === 0 ? "noindex" : "index",
    seo.robots_follow === 0 ? "nofollow" : "follow",
    ra.noarchive ? "noarchive" : null,
    ra.nosnippet ? "nosnippet" : null,
    ra.max_snippet
      ? `max-snippet:${parseInt(ra.max_snippet, 10) || -1}`
      : "max-snippet:-1",
    ra.max_image_preview
      ? `max-image-preview:${ra.max_image_preview}`
      : "max-image-preview:large",
  ].filter(Boolean);
  const robotsContent = robotsParts.join(", ");

  // ─── OG / Twitter ───
  const ogTitle = (seo.og_title || seoTitle).toString();
  const ogDescription = (seo.og_description || seoDescription).toString();
  const ogImageRaw = seo.og_image || ogImageOverride || fallbackImage || "/img/og/siddhivinayak-group-og.jpg";
  const ogImage = absolute(ogImageRaw);
  const twitterTitle = (seo.twitter_title || ogTitle).toString();
  const twitterDescription = (
    seo.twitter_description || ogDescription
  ).toString();
  const twitterImageRaw = seo.twitter_image || ogImageRaw;
  const twitterImage = absolute(twitterImageRaw);
  const twitterCard = seo.twitter_card_type || "summary_large_image";

  // ─── JSON-LD ───
  let schemaJsonString = null;
  if (seo.schema_json) {
    let parsed = null;
    if (typeof seo.schema_json === "string") {
      try {
        parsed = JSON.parse(seo.schema_json);
      } catch (_) {
        parsed = null;
      }
    } else {
      parsed = seo.schema_json;
    }
    // Inject publisher on article schemas when missing — SEO plugins
    // (Detailed SEO, Yoast, Rank Math, etc.) read it from JSON-LD as well.
    if (parsed && type === "article" && publisherName) {
      const ensurePublisher = (node) => {
        if (!node || typeof node !== "object") return;
        const isArticle =
          node["@type"] === "Article" ||
          node["@type"] === "NewsArticle" ||
          node["@type"] === "BlogPosting" ||
          (Array.isArray(node["@type"]) &&
            node["@type"].some((t) =>
              ["Article", "NewsArticle", "BlogPosting"].includes(t)
            ));
        if (isArticle && !node.publisher) {
          node.publisher = {
            "@type": "Organization",
            name: publisherName,
          };
        }
      };
      if (Array.isArray(parsed)) {
        parsed.forEach(ensurePublisher);
      } else if (parsed["@graph"] && Array.isArray(parsed["@graph"])) {
        parsed["@graph"].forEach(ensurePublisher);
      } else {
        ensurePublisher(parsed);
      }
    }
    // Inject a Person author on article schemas when missing — satisfies
    // Google's E-E-A-T expectations and is the headline signal that
    // Rank Math / Yoast also emit. Walk the same tree shapes as the
    // publisher walker above so it works for both top-level schemas and
    // @graph wrappers. Preserves any author already written into the
    // schema by the editor.
    if (parsed && type === "article" && (author || authorName)) {
      const authorPerson = author && typeof author === "object"
        ? buildAuthorPerson(author)
        : { "@type": "Person", name: String(authorName) };
      const ensureAuthor = (node) => {
        if (!node || typeof node !== "object") return;
        const isArticle =
          node["@type"] === "Article" ||
          node["@type"] === "NewsArticle" ||
          node["@type"] === "BlogPosting" ||
          (Array.isArray(node["@type"]) &&
            node["@type"].some((t) =>
              ["Article", "NewsArticle", "BlogPosting"].includes(t)
            ));
        if (isArticle && !node.author) {
          node.author = authorPerson;
        }
      };
      if (Array.isArray(parsed)) {
        parsed.forEach(ensureAuthor);
      } else if (parsed["@graph"] && Array.isArray(parsed["@graph"])) {
        parsed["@graph"].forEach(ensureAuthor);
      } else {
        ensureAuthor(parsed);
      }
    }
    try {
      schemaJsonString = JSON.stringify(parsed);
    } catch (_) {
      schemaJsonString = null;
    }
  }

  // ─── Speakable (AEO) ───
  const speakable = Array.isArray(seo.speakable_selectors)
    ? seo.speakable_selectors.filter((s) => !!s && s !== "")
    : [];

  return (
    <Head>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      {seoKeywords ? <meta name="keywords" content={seoKeywords} /> : null}
      {effectiveAuthorName ? (
        <meta name="author" content={effectiveAuthorName} />
      ) : null}
      {publisherName ? (
        <meta name="publisher" content={publisherName} />
      ) : null}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonical} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      {twitterImage ? (
        <meta name="twitter:image" content={twitterImage} />
      ) : null}

      {/* Article-specific */}
      {type === "article" && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {type === "article" && publishedTime ? (
        <meta property="article:modified_time" content={publishedTime} />
      ) : null}
      {type === "article" && effectiveAuthorName ? (
        <meta property="article:author" content={effectiveAuthorName} />
      ) : null}
      {type === "article" && publisherName ? (
        <meta property="article:publisher" content={publisherName} />
      ) : null}
      {type === "article" && sectionName ? (
        <meta property="article:section" content={sectionName} />
      ) : null}
      {type === "article" &&
        Array.isArray(tagNames) &&
        tagNames.map((t, i) =>
          t ? (
            <meta
              key={`article-tag-${i}`}
              property="article:tag"
              content={String(t)}
            />
          ) : null
        )}

      {/* AEO / Speakable */}
      {speakable.length > 0 ? (
        <meta name="speakable" content={speakable.join(", ")} />
      ) : null}

      {/* JSON-LD structured data */}
      {schemaJsonString ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaJsonString }}
        />
      ) : null}
    </Head>
  );
};

export default SeoHead;
